/**
 * Regras do cartão "Receber avisos?" do Início (aprovado em 02/11/2026 pelo protótipo
 * "Notificações: excluir todas e pedir permissão").
 *
 * O cartão só convida: quem abre a janela de permissão do navegador é o toque em "Ativar
 * notificações" (os navegadores exigem um toque da pessoa, e a decisão de lifecycle de 03/09/2026
 * proíbe ativar sozinho). "Agora não" esconde o cartão e ele volta depois de 7 dias, no máximo
 * 2 vezes (3 aparições no total). A lembrança fica NESTE APARELHO (localStorage), por pessoa, sem
 * nada no banco, no mesmo estilo da Ajuda do Elos (core/ajuda/progresso.ts): trocar de aparelho ou
 * limpar os dados do navegador recomeça do zero, sem prejuízo. A permissão do navegador também é
 * por aparelho, então o desenho combina.
 *
 * Regras puras e tolerantes a falha: localStorage vazio, bloqueado ou com lixo não quebra a tela.
 */

/** Intervalo mínimo entre uma aparição e a seguinte depois de "Agora não". */
export const INTERVALO_REPETIR_MS = 7 * 24 * 60 * 60 * 1000;

/** 1 aparição inicial + 2 repetições. Depois do 3º "Agora não" o cartão não volta mais. */
export const MAX_ADIAMENTOS = 3;

export interface PedidoPermissao {
  /** quantas vezes a pessoa tocou em "Agora não" neste aparelho. */
  adiadas: number;
  /** momento (ms desde 1970) do último "Agora não"; `null` se nunca. */
  ultimaEm: number | null;
}

export const PEDIDO_VAZIO: PedidoPermissao = { adiadas: 0, ultimaEm: null };

type LeitorStorage = Pick<Storage, "getItem">;
type GravadorStorage = Pick<Storage, "setItem">;

/** Uma chave por pessoa: quem divide o aparelho não herda a escolha do outro. */
export function chavePedido(pessoaId: string): string {
  return `elos:push-pedido:v1:${pessoaId}`;
}

export function lerPedido(storage: LeitorStorage | null, chave: string): PedidoPermissao {
  if (!storage) return PEDIDO_VAZIO;
  try {
    const bruto = storage.getItem(chave);
    if (!bruto) return PEDIDO_VAZIO;
    const dado: unknown = JSON.parse(bruto);
    if (typeof dado !== "object" || dado === null) return PEDIDO_VAZIO;
    const { adiadas, ultimaEm } = dado as Record<string, unknown>;
    return {
      adiadas:
        typeof adiadas === "number" && Number.isFinite(adiadas) && adiadas > 0
          ? Math.floor(adiadas)
          : 0,
      ultimaEm: typeof ultimaEm === "number" && Number.isFinite(ultimaEm) ? ultimaEm : null,
    };
  } catch {
    return PEDIDO_VAZIO;
  }
}

export function gravarPedido(
  storage: GravadorStorage | null,
  chave: string,
  pedido: PedidoPermissao,
): void {
  if (!storage) return;
  try {
    storage.setItem(chave, JSON.stringify(pedido));
  } catch {
    // Sem espaço ou bloqueado: o cartão continua funcionando, só não lembra o "Agora não".
  }
}

/** Registra um "Agora não". */
export function adiarPedido(pedido: PedidoPermissao, agoraMs: number): PedidoPermissao {
  return { adiadas: pedido.adiadas + 1, ultimaEm: agoraMs };
}

/** O cartão deve aparecer agora? (Só vale para quem ainda não decidiu a permissão.) */
export function devePedirAgora(pedido: PedidoPermissao, agoraMs: number): boolean {
  if (pedido.adiadas <= 0) return true;
  if (pedido.adiadas >= MAX_ADIAMENTOS) return false;
  if (pedido.ultimaEm === null) return true;
  return agoraMs - pedido.ultimaEm >= INTERVALO_REPETIR_MS;
}

/**
 * iPhone/iPad no navegador (fora da Tela de Início): o Safari só recebe notificação com o Elos
 * instalado. Sem o evento de instalação do Chrome, a única saída é ensinar o passo manual.
 */
export function iosForaDaTelaDeInicio(userAgent: string, standalone: boolean): boolean {
  return /iphone|ipad|ipod/i.test(userAgent) && !standalone;
}

/** `true` quando o Elos está aberto como app instalado (Android, desktop ou Tela de Início do iPhone). */
export function abertoNaTelaDeInicio(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
